/**
 * KollectCare AI - Main Application Entry
 * Professional WhatsApp Engagement Platform
 */

import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useStore } from './store/authStore';
import Layout from './components/Layout';
import './index.css';

// Lazy load pages for better performance
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Campaigns = lazy(() => import('./pages/Campaigns'));
const Workflows = lazy(() => import('./pages/Workflows'));
const Templates = lazy(() => import('./pages/Templates'));
const Analytics = lazy(() => import('./pages/Analytics'));
const Settings = lazy(() => import('./pages/Settings'));
const Inbox = lazy(() => import('./pages/Inbox'));
const Segments = lazy(() => import('./pages/Segments'));
const Broadcasts = lazy(() => import('./pages/Broadcasts'));
const Reports = lazy(() => import('./pages/Reports'));
const Integrations = lazy(() => import('./pages/Integrations'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));

// Loading Spinner Component
function LoadingSpinner() {
  return (
    <div className="page-loading">
      <div className="loading-spinner">
        <div className="spinner-ring"></div>
      </div>
      <p>Loading...</p>
    </div>
  );
}

// Protected Route Component
function ProtectedRoute({ children }) {
  const { user, token } = useStore();
  const location = useLocation();

  if (!user && !token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

// Public Route Component (redirects to dashboard if logged in)
function PublicRoute({ children }) {
  const { user, token } = useStore();
  const location = useLocation();

  if (user || token) {
    const from = location.state?.from?.pathname || '/';
    return <Navigate to={from} replace />;
  }

  return children;
}

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Main App Router
function AppRouter() {
  const { fetchCurrentUser, token } = useStore();

  // Fetch current user on mount if token exists
  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    }
  }, [token, fetchCurrentUser]);

  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Public Routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } 
          />
          <Route path="/forgot-password" element={<Login />} />

          {/* Protected Routes with Layout */}
          <Route
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="contacts" element={<Contacts />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="campaigns/new" element={<Campaigns />} />
            <Route path="campaigns/:id" element={<Campaigns />} />
            <Route path="workflows" element={<Workflows />} />
            <Route path="workflows/new" element={<Workflows />} />
            <Route path="workflows/:id" element={<Workflows />} />
            <Route path="templates" element={<Templates />} />
            <Route path="templates/new" element={<Templates />} />
            <Route path="templates/:id" element={<Templates />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="settings" element={<Settings />} />
            <Route path="settings/:tab" element={<Settings />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="inbox/:conversationId" element={<Inbox />} />
            <Route path="segments" element={<Segments />} />
            <Route path="segments/new" element={<Segments />} />
            <Route path="segments/:id" element={<Segments />} />
            <Route path="broadcasts" element={<Broadcasts />} />
            <Route path="broadcasts/new" element={<Broadcasts />} />
            <Route path="broadcasts/:id" element={<Broadcasts />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/:type" element={<Reports />} />
            <Route path="integrations" element={<Integrations />} />
            <Route path="help" element={<HelpCenter />} />
            <Route path="help/:category" element={<HelpCenter />} />
            <Route path="help/:category/:articleId" element={<HelpCenter />} />
          </Route>

          {/* Catch all - redirect to dashboard or login */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppRouter />
    </BrowserRouter>
  );
}

export default App;
